import React, { useEffect, useState } from 'react';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import { useParams, useHistory } from 'react-router-dom';
import { useRecoilValue, useRecoilState, useSetRecoilState } from 'recoil';
import {
  Button,
  Spin,
  Tabs,
  Dropdown,
  Menu
} from 'antd';
import {
  notificationsAtom,
  myLeadsHeaderAtom,
  reportModalAtom
} from '../../../../atoms/Atoms';
import MainPage from '../../../shared-components/main-page/MainPage';
import { H1 } from '../../../shared-components/typography/Title';
import api from '../../../../api/api';
import { thousand } from '../../../../functions';
import Card from '../../../shared-components/container/Card';
import LeadsPending from './shared-components/LeadsPending';
import LeadsOpen from './shared-components/LeadsOpen';
import LeadsClosed from './shared-components/LeadsClosed';
import LeadsLost from './shared-components/LeadsLost';
import OrgClosersSummaryTable from './shared-components/OrgClosersSummaryTable';
import OrgSettersSummaryTable from './shared-components/OrgSettersSummaryTable';
import PayoutsSummary from './shared-components/PayoutsSummary';
import LeadsSearchTable from './shared-components/LeadsSearchTable';
import InstallMaps from './shared-components/InstallMaps';

function AdminOrgLeads() {
  const { orgReferralCode } = useParams();
  const { TabPane } = Tabs;
  const history = useHistory();
  const { t } = useTranslation();
  const myLeadsHeader = useRecoilValue(myLeadsHeaderAtom);
  const [notifications, setNotifications] = useRecoilState(notificationsAtom);
  const [organization, setOrganization] = useState(null);
  const [loadingOrganization, setLoadingOrganization] = useState(true);
  const [selectedTab, setSelectedTab] = useState('1');
  const [selectedLeadsTab, setSelectedLeadsTab] = useState(1);
  const setReportModal = useSetRecoilState(reportModalAtom);

  function getOrganization() {
    setLoadingOrganization(true);
    api.get(`organizations?rc=${orgReferralCode}`)
        .then((response) => {
          setLoadingOrganization(false);
          if (response?.data?.success) {
            if (response.data.data) {
                setOrganization(response.data.data);
            } else {
              setNotifications([
                ...notifications,
                {
                  title: t('error'),
                  description: t('organization_was_not_found'),
                  error: true,
                },
              ]);
              history.push('/admin');                
            }
          } else {
            setOrganization(null);
            setNotifications([
              ...notifications,
              {
                title: t('error'),
                description: t('organization_was_not_found'),
                error: true,
              },
            ]);
            history.push('/admin');
          }
        })
        .catch((error) => {
          setLoadingOrganization(false);
          history.push('/admin');
          setNotifications([
            ...notifications,
            {
              title: t('error'),
              description: t('organization_was_not_found'),
              error: true,
            },
          ]);
        });
  }

  useEffect(() => {
    if (orgReferralCode) {
      getOrganization();
    }
  }, [orgReferralCode]);

  return (
    <MainPage includeBack requiresCloser>
      <Spin spinning={loadingOrganization}>
        <div className="md:flex mt-4 space-y-2 space-x-2">
          <H1 text={organization?.nameEn} className="mr-auto" />
          <Button
            className="ml-auto w-full md:w-auto"
            type="primary"
            onClick={() => history.push(`/dashboard/${orgReferralCode}`)}
          >
            {t('dashboard')}
          </Button>
          <Dropdown
            overlay={
              <Menu mode="inline" className="p-0">
                <Menu.Item
                  id="watts_sold"
                  className="text-xs flex items-center"
                  onClick={() => {
                    setReportModal({
                      type: 'watts_sold',
                      orgReferralCode
                    });
                  }}
                >
                  {t('watts_sold')}
                </Menu.Item>
              </Menu>
            }
          >
            <Button
              className="w-full md:w-auto"
              type="primary"
            >
              {t('reports')}
            </Button>
          </Dropdown>
          <Button
            className="ml-auto w-full md:w-auto"
            type="primary"
            onClick={() => history.push(`/calendar/${orgReferralCode}`)}
          >
            {t('calendar')}
          </Button>
        </div>
        <Tabs activeKey={selectedTab} onChange={(tab) => setSelectedTab(tab)}>
          <TabPane key="1" tab={t('opportunities')}>
            <div className="grid gap-4 mt-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-5">
              <div className="col-span-1 md:col-span-2 lg:col-span-1 ">
                <Spin spinning={myLeadsHeader?.loading}>
                  <button
                    aria-label={t('all_opportunities')}
                    type="button"
                    className={`w-full border-4 rounded-sm ${selectedLeadsTab === -1 ? 'border-primaryColor' : 'border-transparent'}`}
                    onClick={() => setSelectedLeadsTab(-1)}
                  >
                    <Card
                      title={t('all_opportunities')}
                      description={thousand((myLeadsHeader?.totalNotAssigned +
                                             myLeadsHeader?.totalPendingAssigned +
                                             myLeadsHeader?.totalPendingNoAppointment +
                                             myLeadsHeader?.totalAssigned +
                                             myLeadsHeader?.totalRevisit +
                                             myLeadsHeader?.totalFailedCredit +
                                             myLeadsHeader?.totalInstalled +
                                             myLeadsHeader?.totalActivated +
                                             myLeadsHeader?.totalLost))}
                      fakeChildren
                    />
                  </button>
                </Spin>
              </div>
              <Spin spinning={myLeadsHeader?.loading}>
                <button
                  aria-label={t('leads')}
                  type="button"
                  className={`col-span-1 w-full border-4 rounded-sm ${selectedLeadsTab === 0 ? 'border-primaryColor' : 'border-transparent'}`}
                  onClick={() => setSelectedLeadsTab(0)}
                >
                  <Card
                    title={t('leads')}
                    description={thousand((myLeadsHeader?.totalNotAssigned + myLeadsHeader?.totalPendingAssigned + myLeadsHeader?.totalPendingNoAppointment))}
                    fakeChildren
                  />
                </button>
              </Spin>
              <Spin spinning={myLeadsHeader?.loading}>
                <button
                  aria-label={t('deals')}
                  type="button"
                  className={`col-span-1 w-full border-4 rounded-sm ${selectedLeadsTab === 1 ? 'border-primaryColor' : 'border-transparent'}`}
                  onClick={() => setSelectedLeadsTab(1)}
                >
                  <Card
                    title={t('deals')}
                    description={thousand((myLeadsHeader?.totalAssigned + myLeadsHeader?.totalRevisit + myLeadsHeader?.totalFailedCredit))}
                  >
                    <p className="text-right text-xs"><strong>{t('waiting_signature')}:</strong> {thousand(myLeadsHeader.waitingSignatureKw / 1000)}kw ({thousand(myLeadsHeader.waitingSignatureCount)})</p>
                    <p className="text-right text-xs"><strong>{t('site_survey_completed')}:</strong> {thousand(myLeadsHeader.siteSurveyedKw / 1000)}kw ({thousand(myLeadsHeader.siteSurveyedCount)})</p>
                    <p className="text-right text-xs"><strong>{t('pending_install')}:</strong> {thousand(myLeadsHeader.pendingInstallKw / 1000)}kw ({thousand(myLeadsHeader.pendingInstallCount)})</p>
                  </Card>
                </button>
              </Spin>
              <Spin spinning={myLeadsHeader?.loading}>
                <button
                  aria-label={t('installs')}
                  type="button"
                  className={`col-span-1 w-full border-4 rounded-sm ${selectedLeadsTab === 2 ? 'border-primaryColor' : 'border-transparent'}`}
                  onClick={() => setSelectedLeadsTab(2)}
                >
                  <Card
                    title={t('installs')}
                    description={thousand((myLeadsHeader?.totalInstalled + myLeadsHeader?.totalActivated))}
                  >
                    <p className="text-right text-xs"><strong>{t('installed')}:</strong> {thousand(myLeadsHeader.installedKw / 1000)}kw ({thousand(myLeadsHeader.installedCount)})</p>
                    <p className="text-right text-xs"><strong>{t('PTO')}:</strong> {thousand(myLeadsHeader.activatedKw / 1000)}kw ({thousand(myLeadsHeader.totalActivated)})</p>
                  </Card>
                </button>
              </Spin>
              <Spin spinning={myLeadsHeader?.loading}>
                <button
                  aria-label={t('lost')}
                  type="button"
                  className={`col-span-1 w-full border-4 rounded-sm ${selectedLeadsTab === 3 ? 'border-primaryColor' : 'border-transparent'}`}
                  onClick={() => setSelectedLeadsTab(3)}
                >
                  <Card
                    title={t('lost')}
                    description={thousand(myLeadsHeader?.totalLost)}
                    fakeChildren           
                  />
                </button>
              </Spin>
            </div>
            {selectedLeadsTab === -1 && (
              <LeadsSearchTable orgReferral={orgReferralCode} useGlobal searchType={0} className="mt-2" />
            )}
            {selectedLeadsTab === 0 && (
              <LeadsPending useGlobal orgReferral={orgReferralCode} className="mt-2" />
            )}
            {selectedLeadsTab === 1 && (
              <LeadsOpen useGlobal orgReferral={orgReferralCode} className="mt-2" />
            )}
            {selectedLeadsTab === 2 && (
              <LeadsClosed useGlobal orgReferral={orgReferralCode} className="mt-2" />
            )}
            {selectedLeadsTab === 3 && (
              <LeadsLost useGlobal orgReferral={orgReferralCode} className="mt-2" />
            )}
          </TabPane>
          <TabPane key="2" tab={t('closers')}>
            <OrgClosersSummaryTable orgCode={orgReferralCode} />
          </TabPane>
          <TabPane key="3" tab={t('setters')}>
            <OrgSettersSummaryTable orgCode={orgReferralCode} />
          </TabPane>
          <TabPane key="4" tab={t('payouts')}>
            <PayoutsSummary orgCode={orgReferralCode} />
          </TabPane>
          <TabPane key="5" tab={t('installs_map')}>
            <InstallMaps orgCode={orgReferralCode} />
          </TabPane>
        </Tabs>
      </Spin>
    </MainPage>
  );
}

export default AdminOrgLeads;
